import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  InputNumber,
  Modal,
  Space,
  Typography,
  List,
  Tooltip,
  Tag,
  Slider,
} from 'antd';
import {
  BgColorsOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useObjectsApi } from '../../../api/objects/objects';
import { TPuzzleLessonComponent, TPuzzleLessonConfig, createEmptyPuzzleLessonConfig } from '../../../models/courses/puzzleLesson';
import type { TImage } from '../../../models/objects/media';
import './PuzzleLessonAdmin.css';
import JsonEditor from '../../JsonEditor/JsonEditor';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type MediaPickTarget = 'background' | { kind: 'component'; id: string };
type TransformMode = 'none' | 'move' | 'resize';
type ResizeHandle = 'nw' | 'n' | 'ne' | 'w' | 'e' | 'sw' | 's' | 'se';

interface PuzzleLessonAdminProps {
  config: TPuzzleLessonConfig;
  onConfigChange: (config: TPuzzleLessonConfig) => void;
  onSave: () => void;
  saving?: boolean;
  initialStatement?: string;
  onStatementChange?: (value: string) => void;
}

const createId = () => `piece-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const PuzzleLessonAdmin: React.FC<PuzzleLessonAdminProps> = ({
  config,
  onConfigChange,
  onSave,
  saving = false,
  initialStatement,
  onStatementChange,
}) => {
  const { t } = useTranslation();
  const objectsApi = useObjectsApi();
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [images, setImages] = useState<TImage[]>([]);
  const [mediaTarget, setMediaTarget] = useState<MediaPickTarget>('background');
  const [jsonOpen, setJsonOpen] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [mediaSearch, setMediaSearch] = useState<string>('');
  const filteredImages = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();
    if (!q) return images;
    return images.filter((img) =>
      (img.file_name || '').toLowerCase().includes(q) || (img.file_path || '').toLowerCase().includes(q)
    );
  }, [images, mediaSearch]);
  
  // Transform state
  const [transformMode, setTransformMode] = useState<TransformMode>('none');
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; component: TPuzzleLessonComponent } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);

  const safeConfig = useMemo(
    () => ({ ...createEmptyPuzzleLessonConfig(), ...config, components: config.components || [] }),
    [config],
  );

  const updateConfig = (updater: (prev: TPuzzleLessonConfig) => TPuzzleLessonConfig) => {
    const next = updater(safeConfig);
    onConfigChange(next);
  };

  const resetConfig = () => {
    Modal.confirm({
      title: t('puzzleLesson.admin.confirmResetTitle') || 'Réinitialiser la configuration',
      content: t('puzzleLesson.admin.confirmResetContent') || 'Êtes-vous sûr ?',
      onOk: () => {
        onConfigChange(createEmptyPuzzleLessonConfig());
        setSelectedComponentId(null);
      },
    });
  };

  const addComponent = () => {
    const newComponent: TPuzzleLessonComponent = {
      id: createId(),
      keyword: '',
      image: '',
      x: 10,
      y: 10,
      width: 20,
      height: 20,
    };
    updateConfig((prev) => ({
      ...prev,
      components: [...(prev.components || []), newComponent],
    }));
    setSelectedComponentId(newComponent.id);
  };

  const removeComponent = (id: string) => {
    updateConfig((prev) => ({
      ...prev,
      components: (prev.components || []).filter((c) => c.id !== id),
    }));
    if (selectedComponentId === id) setSelectedComponentId(null);
  };

  const updateComponent = (id: string, data: Partial<TPuzzleLessonComponent>) => {
    updateConfig((prev) => ({
      ...prev,
      components: (prev.components || []).map((c) => (c.id === id ? { ...c, ...data } : c)),
    }));
  };

  const handleBoardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only handle click-to-place if not dragging
    if (isDragging) return;
    if (!selectedComponentId || !safeConfig.backgroundImage) return;
    
    // Check if click was on a component or handle (should be handled by component's handlers)
    const target = event.target as HTMLElement;
    if (target.closest('.puzzle-piece-wrapper') || target.closest('.transform-handle')) return;
    
    const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    updateComponent(selectedComponentId, {
      x: Math.min(100, Math.max(0, parseFloat(x.toFixed(2)))),
      y: Math.min(100, Math.max(0, parseFloat(y.toFixed(2)))),
    });
  };

  // Transform handlers
  const handlePieceMouseDown = useCallback((e: React.MouseEvent, component: TPuzzleLessonComponent) => {
    if (!boardRef.current) return;
    e.stopPropagation();
    
    const rect = boardRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      component: { ...component },
    });
    setTransformMode('move');
    setIsDragging(true);
  }, []);

  const handleResizeHandleMouseDown = useCallback((e: React.MouseEvent, handle: ResizeHandle, component: TPuzzleLessonComponent) => {
    if (!boardRef.current) return;
    e.stopPropagation();
    
    setResizeHandle(handle);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      component: { ...component },
    });
    setTransformMode('resize');
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragStart || !boardRef.current || !selectedComponentId) return;

    const boardRect = boardRef.current.getBoundingClientRect();
    const boardWidth = boardRect.width;
    const boardHeight = boardRect.height;

    if (transformMode === 'move') {
      const deltaX = ((e.clientX - dragStart.x) / boardWidth) * 100;
      const deltaY = ((e.clientY - dragStart.y) / boardHeight) * 100;

      const newX = Math.min(100, Math.max(0, dragStart.component.x + deltaX));
      const newY = Math.min(100, Math.max(0, dragStart.component.y + deltaY));

      updateComponent(selectedComponentId, {
        x: parseFloat(newX.toFixed(2)),
        y: parseFloat(newY.toFixed(2)),
      });
    } else if (transformMode === 'resize' && resizeHandle) {
      const deltaX = ((e.clientX - dragStart.x) / boardWidth) * 100;
      const deltaY = ((e.clientY - dragStart.y) / boardHeight) * 100;

      let newX = dragStart.component.x;
      let newY = dragStart.component.y;
      let newWidth = dragStart.component.width;
      let newHeight = dragStart.component.height;

      // Handle resize based on handle position
      if (resizeHandle.includes('w')) {
        newX = Math.min(100, Math.max(0, dragStart.component.x + deltaX));
        newWidth = Math.max(5, Math.min(100, dragStart.component.width - deltaX));
      }
      if (resizeHandle.includes('e')) {
        newWidth = Math.max(5, Math.min(100, dragStart.component.width + deltaX));
      }
      if (resizeHandle.includes('n')) {
        newY = Math.min(100, Math.max(0, dragStart.component.y + deltaY));
        newHeight = Math.max(5, Math.min(100, dragStart.component.height - deltaY));
      }
      if (resizeHandle.includes('s')) {
        newHeight = Math.max(5, Math.min(100, dragStart.component.height + deltaY));
      }

      // Constrain to board boundaries
      if (newX + newWidth > 100) {
        newWidth = 100 - newX;
      }
      if (newY + newHeight > 100) {
        newHeight = 100 - newY;
      }

      updateComponent(selectedComponentId, {
        x: parseFloat(newX.toFixed(2)),
        y: parseFloat(newY.toFixed(2)),
        width: parseFloat(newWidth.toFixed(2)),
        height: parseFloat(newHeight.toFixed(2)),
      });
    }
  }, [isDragging, dragStart, transformMode, resizeHandle, selectedComponentId, updateComponent]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setTransformMode('none');
      setResizeHandle(null);
      setDragStart(null);
    }
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const openMediaPicker = (target: MediaPickTarget) => {
    setMediaTarget(target);
    setMediaPickerOpen(true);
    setMediaLoading(true);
    objectsApi.getImages()
      .then((resp) => setImages(resp.data || []))
      .catch((err: any) => {
        Modal.error({
          title: t('puzzleLesson.admin.mediaLoadErrorTitle') || 'Erreur de chargement',
          content: err?.response?.data || err?.message || 'Impossible de charger les images.',
        });
      })
      .finally(() => setMediaLoading(false));
  };

  const handleMediaSelect = (filePath: string) => {
    if (mediaTarget === 'background') {
      setImageError(null); // Reset error when selecting new image
      updateConfig((prev) => ({ ...prev, backgroundImage: filePath }));
    } else {
      updateComponent(mediaTarget.id, { image: filePath });
    }
    setMediaPickerOpen(false);
  };

  useEffect(() => {
    if (!safeConfig.components.find((c) => c.id === selectedComponentId)) {
      setSelectedComponentId(null);
      // Reset transform state when component is removed
      setTransformMode('none');
      setResizeHandle(null);
      setDragStart(null);
      setIsDragging(false);
    }
  }, [safeConfig.components, selectedComponentId]);

  return (
    <div className="puzzle-admin">
      <Card
        title={<Title level={4}>{t('puzzleLesson.admin.title') || 'Configuration Lesson Puzzle'}</Title>}
        extra={(
          <Space>
            <Button icon={<EditOutlined />} onClick={() => setJsonOpen(true)}>
              {t('aiModelBuilder.admin.openJsonEditor') || 'Éditeur JSON'}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={resetConfig}>
              {t('puzzleLesson.admin.reset') || 'Réinitialiser'}
            </Button>
            <Button icon={<SaveOutlined />} type="primary" loading={saving} onClick={onSave}>
              {t('puzzleLesson.admin.save') || 'Sauvegarder'}
            </Button>
          </Space>
        )}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Paragraph type="secondary">
            {t('puzzleLesson.admin.description') ||
              'Associez des mots-clés à des pièces. Les élèves placeront ces mots dans leur prompt pour révéler les images sur l’arrière-plan.'}
          </Paragraph>

          <div className="puzzle-section">
            <Title level={5}>{t('puzzleLesson.admin.statement') || 'Énoncé'}</Title>
            <TextArea
              rows={3}
              value={initialStatement ?? safeConfig.statement}
              placeholder={t('puzzleLesson.admin.statementPlaceholder') || 'Saisissez l’énoncé visible par l’élève'}
              onChange={(e) => {
                updateConfig((prev) => ({ ...prev, statement: e.target.value }));
                onStatementChange?.(e.target.value);
              }}
            />
          </div>

          <div className="puzzle-section">
            <Title level={5}>{t('puzzleLesson.admin.background') || "Image d'arrière-plan"}</Title>
            <Input
              value={safeConfig.backgroundImage}
              placeholder={t('puzzleLesson.admin.backgroundPlaceholder') || "Chemin ou URL de l'image"}
              onChange={(e) => updateConfig((prev) => ({ ...prev, backgroundImage: e.target.value }))}
              addonAfter={(
                <Button icon={<PictureOutlined />} onClick={() => openMediaPicker('background')}>
                  {t('puzzleLesson.admin.chooseBackground') || 'Choisir'}
                </Button>
              )}
            />
            <Space style={{ marginTop: 12 }} align="center">
              <Text strong>{t('puzzleLesson.admin.placeholderOpacity') || 'Opacité des ombres'}</Text>
              <Slider
                min={0}
                max={1}
                step={0.05}
                value={safeConfig.placeholderOpacity}
                style={{ width: 220 }}
                onChange={(value) => updateConfig((prev) => ({ ...prev, placeholderOpacity: value }))}
              />
            </Space>
          </div>

          <div className="puzzle-section">
            <Flex align="center" justify="space-between">
              <Title level={5}>{t('puzzleLesson.admin.components') || 'Composants'}</Title>
              <Button icon={<PlusOutlined />} type="primary" onClick={addComponent}>
                {t('puzzleLesson.admin.addComponent') || 'Ajouter un composant'}
              </Button>
            </Flex>
            {safeConfig.components.length === 0 && (
              <Alert
                type="info"
                showIcon
                message={t('puzzleLesson.admin.noComponents') || 'Aucun composant pour le moment.'}
                style={{ marginTop: 12 }}
              />
            )}
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {safeConfig.components.map((component) => (
                <Card
                  key={component.id}
                  size="small"
                  className={selectedComponentId === component.id ? 'puzzle-component-card active' : 'puzzle-component-card'}
                  title={(
                    <Space>
                      <BgColorsOutlined />
                      <span>{t('puzzleLesson.admin.component') || 'Composant'}</span>
                      {component.keyword && <Tag color="blue">{component.keyword}</Tag>}
                    </Space>
                  )}
                  extra={(
                    <Space>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => setSelectedComponentId(component.id)}
                        title={t('puzzleLesson.admin.selectForPlacement') || 'Sélectionner pour placer'}
                      />
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeComponent(component.id)}
                      />
                    </Space>
                  )}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Input
                      value={component.keyword}
                      placeholder={t('puzzleLesson.admin.keywordPlaceholder') || 'Mot-clé déclencheur'}
                      onChange={(e) => updateComponent(component.id, { keyword: e.target.value })}
                    />
                    <Input
                      value={component.image}
                      placeholder={t('puzzleLesson.admin.componentImagePlaceholder') || "Chemin ou URL de l'image"}
                      onChange={(e) => updateComponent(component.id, { image: e.target.value })}
                      addonAfter={(
                        <Button
                          icon={<PictureOutlined />}
                          onClick={() => openMediaPicker({ kind: 'component', id: component.id })}
                        >
                          {t('puzzleLesson.admin.chooseMedia') || 'Choisir'}
                        </Button>
                      )}
                    />
                    <Space>
                      <InputNumber
                        addonBefore="X"
                        min={0}
                        max={100}
                        value={component.x}
                        onChange={(value) => updateComponent(component.id, { x: value || 0 })}
                      />
                      <InputNumber
                        addonBefore="Y"
                        min={0}
                        max={100}
                        value={component.y}
                        onChange={(value) => updateComponent(component.id, { y: value || 0 })}
                      />
                      <InputNumber
                        addonBefore={t('puzzleLesson.admin.width') || 'Larg.'}
                        min={5}
                        max={100}
                        value={component.width}
                        onChange={(value) => updateComponent(component.id, { width: value || 0 })}
                      />
                      <InputNumber
                        addonBefore={t('puzzleLesson.admin.height') || 'Haut.'}
                        min={5}
                        max={100}
                        value={component.height}
                        onChange={(value) => updateComponent(component.id, { height: value || 0 })}
                      />
                    </Space>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>

          <div className="puzzle-section">
            <Flex align="center" justify="space-between">
              <Title level={5}>{t('puzzleLesson.admin.preview') || 'Aperçu & placement'}</Title>
              <Space>
                <Text type="secondary">
                  {t('puzzleLesson.admin.clickHint') || 'Sélectionnez un composant puis cliquez sur le fond pour le placer.'}
                </Text>
              </Space>
            </Flex>

            <div
              ref={boardRef}
              className={`puzzle-board ${isDragging ? 'dragging' : ''}`}
              onClick={handleBoardClick}
            >
              {safeConfig.backgroundImage ? (
                <>
                  <img
                    key={safeConfig.backgroundImage}
                    src={safeConfig.backgroundImage}
                    alt="Puzzle background"
                    className="puzzle-background-image"
                    style={{ opacity: 1 }}
                    onError={(e) => {
                      console.error('Failed to load background image:', safeConfig.backgroundImage);
                      setImageError(safeConfig.backgroundImage || null);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Background image loaded successfully:', safeConfig.backgroundImage);
                      setImageError(null);
                    }}
                  />
                  {imageError && (
                    <div className="puzzle-board-empty">
                      <Alert
                        type="error"
                        message="Erreur de chargement de l'image"
                        description={
                          <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Impossible de charger: {imageError.substring(0, 60)}...
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                              Vérifiez que le chemin ou l'URL est correct.
                            </Text>
                          </div>
                        }
                        style={{ margin: '16px' }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="puzzle-board-empty">
                  <PictureOutlined style={{ fontSize: 32 }} />
                  <Text type="secondary">
                    {t('puzzleLesson.admin.noBackground') || 'Choisissez une image pour commencer le placement.'}
                  </Text>
                </div>
              )}
              {safeConfig.components.map((component) => {
                const isActive = selectedComponentId === component.id;
                return (
                  <div
                    key={component.id}
                    className={`puzzle-piece-wrapper ${isActive ? 'active' : ''} ${isDragging && isActive ? 'dragging' : ''}`}
                    style={{
                      left: `${component.x}%`,
                      top: `${component.y}%`,
                      width: `${component.width}%`,
                      height: `${component.height}%`,
                    }}
                    onMouseDown={(e) => isActive && handlePieceMouseDown(e, component)}
                  >
                    <div
                      className={isActive ? 'puzzle-piece shadow active' : 'puzzle-piece shadow'}
                      style={{
                        width: '100%',
                        height: '100%',
                        opacity: 1,
                        backgroundImage: component.image ? `url(${component.image})` : undefined,
                        cursor: isActive ? (isDragging ? 'grabbing' : 'grab') : 'default',
                      }}
                    >
                      <div className="puzzle-piece-label">
                        <Text>{component.keyword || t('puzzleLesson.admin.placeholderKeyword') || 'Mot-clé'}</Text>
                      </div>
                    </div>
                    {isActive && (
                      <>
                        {/* Corner handles */}
                        <div
                          className="transform-handle handle-nw"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 'nw', component)}
                        />
                        <div
                          className="transform-handle handle-ne"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 'ne', component)}
                        />
                        <div
                          className="transform-handle handle-sw"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 'sw', component)}
                        />
                        <div
                          className="transform-handle handle-se"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 'se', component)}
                        />
                        {/* Edge handles */}
                        <div
                          className="transform-handle handle-n"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 'n', component)}
                        />
                        <div
                          className="transform-handle handle-s"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 's', component)}
                        />
                        <div
                          className="transform-handle handle-w"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 'w', component)}
                        />
                        <div
                          className="transform-handle handle-e"
                          onMouseDown={(e) => handleResizeHandleMouseDown(e, 'e', component)}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="puzzle-section">
            <Title level={5}>{t('puzzleLesson.admin.successRule') || 'Condition de réussite'}</Title>
            <Space>
              <InputNumber
                min={0}
                max={safeConfig.components.length || 0}
                value={safeConfig.requiredMatches}
                onChange={(value) => updateConfig((prev) => ({ ...prev, requiredMatches: value ?? 0 }))}
              />
              <Text type="secondary">
                {t('puzzleLesson.admin.requiredHint') ||
                  'Nombre de pièces à découvrir pour valider la leçon. 0 = toutes les pièces.'}
              </Text>
            </Space>
          </div>
        </Space>
      </Card>

      {/* JSON Editor Modal */}
      <Modal
        open={jsonOpen}
        onCancel={() => setJsonOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <JsonEditor<TPuzzleLessonConfig>
          title={t('puzzleLesson.admin.jsonTitle') || 'JSON de configuration'}
          value={safeConfig}
          showStatement
          initialStatement={initialStatement ?? safeConfig.statement ?? ''}
          onStatementChange={(val) => {
            onStatementChange?.(val);
            updateConfig((prev) => ({ ...prev, statement: val }));
          }}
          onApply={(data) => {
            onConfigChange({ ...createEmptyPuzzleLessonConfig(), ...data });
            onStatementChange?.(data.statement || '');
            setJsonOpen(false);
          }}
          minRows={16}
        />
      </Modal>

      <Modal
        open={mediaPickerOpen}
        onCancel={() => setMediaPickerOpen(false)}
        footer={null}
        width={720}
        title={t('puzzleLesson.admin.mediaTitle') || 'Sélectionner une image'}
      >
        <List
          loading={mediaLoading}
          header={(<Input.Search placeholder={t('puzzleLesson.admin.searchPlaceholder') || 'Rechercher par nom de fichier'} allowClear value={mediaSearch} onChange={(e) => setMediaSearch(e.target.value)} />)}
          dataSource={filteredImages}
          rowKey={(item: TImage) => String(item.id)}
          renderItem={(item: TImage) => (
            <List.Item
              actions={[
                <Button
                  type="link"
                  onClick={() => handleMediaSelect(item.file_path)}
                  key="select"
                  icon={<CheckCircleOutlined />}
                >
                  {t('puzzleLesson.admin.select') || 'Sélectionner'}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={(
                  <div className="puzzle-media-thumb">
                    <img src={item.file_path} alt={item.file_name} />
                  </div>
                )}
                title={(
                  <Tooltip title={item.file_name || item.file_path}>
                    <Text>{item.file_name || item.file_path}</Text>
                  </Tooltip>
                )}
                description={item.file_path}
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default PuzzleLessonAdmin;
